import { ApiError, type ClassificationJob, type ClassificationResult } from '../../lib/api';
import type { EvidenceItem, ReconciliationItem } from './types';
import { isIOSPath } from '../../lib/web-presentation';
import { evaluateExtractedOwnership, type EvidenceOwnerCandidate } from './evidence-ownership';

export function classificationSourceUnavailable(job: ClassificationJob): boolean {
  return ['source_missing', 'source_unverified', 'invalid_storage_reference'].includes(job.failure_code ?? '');
}

export function settleEvidence(item: EvidenceItem, job: ClassificationJob): EvidenceItem {
  if (job.status === 'completed' && item.ownershipState === 'mismatch') return { ...item, state: 'blocked',
    issue: item.issue ?? 'Possible wrong taxpayer. View the document and resolve ownership before reviewing its proposals.' };
  if (job.status === 'completed') return { ...item, state: 'ready-for-review',
    issue: `${job.transaction_count} source-derived proposal${job.transaction_count === 1 ? ' requires' : 's require'} human review` };
  if (job.status === 'failed') return { ...item, state: 'processing-failed',
    processingRetryable: false,
    ...(classificationSourceUnavailable(job)
      ? { documentAvailable: false } : {}), issue: classificationFailureMessage(job) };
  return item;
}

/** Derives the source-level label from its item-level human decisions. A source
 * must not stay visually blocked after every usable item has a recorded review,
 * and a displayed/extracted amount never clears a real blocker by itself. */
export function applyEvidenceReviewProgress(evidence: readonly EvidenceItem[],
  reconciliation: readonly ReconciliationItem[], evidenceId?: string): EvidenceItem[] {
  if (!evidenceId) return [...evidence];
  const related = reconciliation.filter((item) => item.evidenceId === evidenceId);
  if (related.length === 0) return [...evidence];
  return evidence.map((source) => {
    if (source.id !== evidenceId || source.state === 'unlinked' || source.state === 'superseded'
      || source.periodState === 'mismatch' || source.duplicateOfEvidenceId) return source;
    const blocked = related.filter((item) => item.state === 'blocked').length;
    const pending = related.filter((item) => item.state === 'needs-review').length;
    if (blocked > 0) return { ...source, state: 'blocked',
      issue: source.issue ?? `${blocked} extracted item${blocked === 1 ? ' is' : 's are'} blocked and cannot be used yet.` };
    if (pending > 0) return { ...source, state: 'ready-for-review',
      issue: `${pending} extracted item${pending === 1 ? ' still needs' : 's still need'} your review before its figures can be used.` };
    return { ...source, state: 'reviewed',
      issue: 'All extracted items have a recorded human decision. Only accepted or corrected figures can be used.' };
  });
}

/** Keeps a durable source truthful when the browser loses contact with a
 * later workflow stage. A polling timeout or temporary API interruption is
 * not a failed extraction/classification job. */
export function settleEvidenceWorkflowError(item: EvidenceItem, error: unknown): EvidenceItem {
  if (error instanceof ApiError && (error.code === 'poll_timeout' || error.retryable)) {
    return { ...item, state: 'processing-delayed', processingRetryable: true, issue: error.code === 'poll_timeout'
      ? 'Analysis is taking longer than expected. Your document is saved; do not upload it again. Use Resume processing in Source actions to check its existing job. You can continue with manually entered tax amounts.'
      : 'Processing was interrupted temporarily. Amoye has kept the secured source and can continue from the durable job.' };
  }
  const supportReference = error instanceof ApiError && error.correlationId
    ? ` Support reference: ${error.correlationId}.` : '';
  if (error instanceof ApiError && error.status === 403) return { ...item, state: 'processing-failed',
    issue: `The secured source could not be linked to the currently selected taxpayer and filing period. Confirm your signed-in product and selected taxpayer, then try again.${supportReference}` };
  if (error instanceof ApiError && error.status === 409) return { ...item, state: 'processing-failed',
    issue: `The secured source conflicts with the currently selected taxpayer, period, or an earlier processing step. Keep the source and review the selected context before retrying.${supportReference}` };
  return { ...item, state: 'processing-failed',
    issue: `Amoye secured the source but could not finish preparing its review items. ${error instanceof Error ? error.message : 'Check the service before retrying.'}${supportReference}` };
}

function classificationFailureMessage(job: ClassificationJob): string {
  const detail = job.failure_detail ?? '';
  if (job.failure_code === 'source_missing') return 'The stored document is unavailable. Contact support to restore its original source or add a replacement. You can continue with manual tax amounts.';
  if (job.failure_code === 'source_unverified' || job.failure_code === 'invalid_storage_reference') {
    return 'This stored source could not be verified. Contact support before retrying, or add a new supported source. You can continue with manual tax amounts.';
  }
  if (detail.includes('OCR transaction amount must be greater than zero')) {
    return 'Amoye found a possible transaction, but could not validate its main amount. View the document, correct the source if needed, then retry it.';
  }
  if (job.failure_code === 'unsupported_transaction_json' || detail.includes('canonical transaction export version 1.0')
    || detail.includes('This JSON file is not a supported transaction export')) {
    return 'This JSON file is not a supported transaction export. Use transaction JSON with schema_version "1.0" and a transactions array, or a supported CSV/document. Retrying the same file will not fix its format. You can continue with manual tax amounts.';
  }
  if (job.failure_code === 'extraction_required') {
    return 'Amoye could not extract a reliable transaction from this source. View it, then replace it with a clearer copy or structured export.';
  }
  if (job.failure_code === 'trusted_file_type_required') {
    return 'The stored file type could not be verified. Remove this source and upload it again using a supported document format.';
  }
  return 'No reliable transaction proposal was created. View the document and check its amount, date and transaction direction before retrying it.';
}

export function applyExtractedOwnership(item: EvidenceItem, result: ClassificationResult,
  selected: EvidenceOwnerCandidate): { evidence: EvidenceItem; blockProposals: boolean } {
  const ownership = evaluateExtractedOwnership(result.document_analysis, selected);
  const analysis = result.document_analysis;
  const duplicate = analysis?.duplicate_of_job_id;
  const serverMismatch = analysis?.ownership_state === 'mismatch';
  const serverNeedsConfirmation = analysis?.ownership_state === 'needs_confirmation';
  const metadata = analysis ? {
    ...(analysis.document_category ? { classificationCategory: analysis.document_category } : {}),
    ...(analysis.classification_schema_version ? { classificationSchemaVersion: analysis.classification_schema_version } : {}),
    ...(analysis.period_state ? { periodState: analysis.period_state } : {}),
    ...(duplicate ? { duplicateOfJobId: duplicate } : {}),
    ...(analysis.review_reason_codes ? { reviewReasonCodes: analysis.review_reason_codes } : {}),
  } : {};
  if (!ownership) return item.ownershipState === 'mismatch' || item.ownershipState === 'unverified'
    || serverMismatch
    ? { evidence: { ...item, ...metadata, state: 'blocked' }, blockProposals: true }
    : { evidence: { ...item, ...metadata, issue: analysis?.period_state === 'mismatch'
      ? 'One or more extracted items fall outside the selected filing period. Review each period warning before tax use.' : item.issue }, blockProposals: false };
  const ownershipProposal = { extractor: ownership.extractor,
    ...(ownership.detectedName ? { detectedName: ownership.detectedName } : {}),
    ...(ownership.taxIdentifier ? { taxIdentifier: ownership.taxIdentifier } : {}),
    ...(ownership.confidence ? { confidence: ownership.confidence } : {}),
    ...(ownership.basis ? { basis: ownership.basis } : {}) };
  const needsOwnershipConfirmation = ownership.state === 'unverified' || serverNeedsConfirmation;
  if (needsOwnershipConfirmation && item.ownershipState === 'mismatch' && item.ownershipProposal) {
    return { blockProposals: true, evidence: { ...item, ...metadata, state: 'blocked', ownershipState: 'mismatch',
      issue: `Source analysis found ${item.ownershipProposal.detectedName ?? 'a possible different owner'}. View the source and confirm the selected taxpayer or flag it as wrong.` } };
  }
  if (ownership.state === 'mismatch' || serverMismatch || needsOwnershipConfirmation) return { blockProposals: true, evidence: { ...item, ...metadata, state: 'blocked',
    ownershipState: needsOwnershipConfirmation ? 'unverified' : 'mismatch', ownershipProposal,
    issue: needsOwnershipConfirmation
      ? `Source analysis could not safely determine one owner${ownership.detectedName ? ` from ${ownership.detectedName}` : ''}. View the source, then choose whether it belongs to the selected taxpayer.`
      : `Source analysis found ${ownership.detectedName ?? 'a different labelled owner'}. View the source and confirm the selected taxpayer or flag it as wrong.` } };
  if (analysis?.period_state === 'mismatch') return { blockProposals: true, evidence: { ...item, ...metadata, state: 'blocked',
    ownershipState: ownership.state, ownershipProposal,
    issue: 'This source appears outside the selected filing period. Move it to the correct period or remove it from this tax record.' } };
  if (duplicate) return { blockProposals: true, evidence: { ...item, ...metadata, state: 'blocked',
    issue: 'This source has the same secured content as an earlier upload. Remove this copy or continue with the original source.' } };
  return { blockProposals: false, evidence: { ...item, ...metadata, ownershipState: ownership.state, ownershipProposal,
    issue: ownership.state === 'matched'
      ? `${result.transactions.length} proposals require review; source analysis found a labelled owner matching the selected taxpayer.`
      : `${result.transactions.length} proposals require review; source analysis did not find a reliable labelled owner.` } };
}

/** Applies all source blockers without allowing a lower-priority duplicate
 * warning to hide an ownership or filing-period problem. */
export function applyEvidenceProposalBlockers(item: ReconciliationItem, evidence: EvidenceItem | undefined,
  duplicateOfEvidenceId?: string): ReconciliationItem {
  if (evidence?.ownershipState === 'mismatch' || evidence?.ownershipState === 'unverified') return {
    ...item, state: 'blocked', dispositionReason: evidence.ownershipState === 'mismatch'
      ? 'Resolve the possible taxpayer mismatch before reviewing this proposal.'
      : 'Confirm which taxpayer owns this source before reviewing this proposal.',
  };
  if (item.periodState === 'mismatch' || evidence?.periodState === 'mismatch') return {
    ...item, state: 'blocked', dispositionReason: 'This item is outside the selected filing period.',
  };
  if (duplicateOfEvidenceId || evidence?.duplicateOfJobId) return {
    ...item, state: 'blocked', dispositionReason: 'Duplicate of an existing secured source.',
  };
  return item;
}

export function applyEvidenceDuplicatePresentation(item: EvidenceItem,
  duplicateOfEvidenceId?: string): EvidenceItem {
  if (!duplicateOfEvidenceId) return item;
  const priorIssue = item.ownershipState === 'mismatch' || item.ownershipState === 'unverified'
    || item.periodState === 'mismatch' ? `${item.issue ?? ''} ` : '';
  return { ...item, state: 'blocked', duplicateOfEvidenceId,
    issue: `${priorIssue}This copy duplicates an existing secured source and has no separate review record. Continue with the original source or remove this copy.`.trim() };
}

export function reconciliationFromResult(result: ClassificationResult, sourceLabel: string,
  evidenceId?: string): ReconciliationItem[] {
  const transactions = result.transactions.map((transaction) => {
    const classification = transaction.classification;
    const periodMismatch = classification?.period_state === 'mismatch';
    const modelAssisted = classification?.reason_codes.includes('MODEL_ASSISTED_CANDIDATE') ?? false;
    const proposalMethod = modelAssisted ? 'model-assisted' as const
      : classification?.rule_id && !classification.rule_id.endsWith('REVIEW') ? 'deterministic-rule' as const
        : 'unclassified' as const;
    return {
      id: transaction.id,
      description: transaction.description ?? 'Source-derived transaction proposal',
      amountLabel: formatKobo(transaction.amount_kobo),
      sourceLabel: `${sourceLabel} · ${transaction.transaction_date} · ${transaction.direction}`,
      proposedTreatment: classification
        ? `${modelAssisted ? 'Optional model suggestion · ' : ''}${classification.treatment_label}${transaction.nta_code ? ` · ${transaction.nta_code}` : ''}`
        : transaction.nta_code ? `Bookkeeping category proposal: ${transaction.nta_code}` : 'No category proposed',
      confidence: confidenceBand(transaction.confidence),
      state: periodMismatch ? 'blocked' as const : 'needs-review' as const,
      reason: periodMismatch
        ? 'This item is outside the selected filing period. Move it to the correct period or exclude it before tax use.'
        : transaction.review_status === 'needs_review'
          ? modelAssisted
            ? 'An approved model selected from Amoye’s closed category list. Confirm or correct it; the suggestion is not a tax fact or calculation.'
            : 'Source extraction or classification output is a proposal only. Confirm or correct it before it becomes a reviewed fact.'
          : 'A recorded bookkeeping rule matched. Human acceptance is still required before tax preparation.',
      transactionDate: transaction.transaction_date,
      direction: transaction.direction,
      amountKobo: transaction.amount_kobo,
      vatAmountKobo: transaction.vat_amount_kobo,
      ...(transaction.nta_code ? { ntaCode: transaction.nta_code } : {}),
      ...(classification?.evidence_category ? { evidenceCategory: classification.evidence_category } : {}),
      ...(classification?.period_state ? { periodState: classification.period_state } : {}),
      ...(classification?.schema_version ? { classificationSchemaVersion: classification.schema_version } : {}),
      ...(classification?.rule_id ? { ruleId: classification.rule_id } : {}),
      proposalMethod,
      ...(periodMismatch ? { dispositionReason: 'Outside the selected filing period' } : {}),
      ...(evidenceId ? { evidenceId } : {}),
    };
  });
  const analysis = result.document_analysis;
  if (!analysis?.document_category) return transactions;
  const periodMismatch = analysis.period_state === 'mismatch';
  return [{
    id: result.job.job_id,
    description: 'Document type and tax use',
    amountLabel: '—',
    sourceLabel,
    proposedTreatment: `${isIOSPath() ? 'Evidence category' : 'Document category'}: ${analysis.document_category.replaceAll('-', ' ')}`,
    confidence: analysis.ownership_confidence ? confidenceBand(analysis.ownership_confidence) : 'medium',
    state: periodMismatch ? 'blocked' : 'needs-review',
    reason: periodMismatch
      ? 'This document appears outside the selected filing period. Move it to the correct period or exclude it.'
      : 'Amoye proposed how this document should be used. Confirm or correct the category before tax use.',
    evidenceCategory: analysis.document_category,
    ...(analysis.period_state ? { periodState: analysis.period_state } : {}),
    ...(analysis.classification_schema_version ? { classificationSchemaVersion: analysis.classification_schema_version } : {}),
    ...(periodMismatch ? { dispositionReason: 'Outside the selected filing period' } : {}),
    ...(evidenceId ? { evidenceId } : {}),
  }, ...transactions];
}

function confidenceBand(value: string | null): ReconciliationItem['confidence'] {
  const confidence = value === null ? 0 : Number(value);
  return confidence >= 0.85 ? 'high' : confidence >= 0.6 ? 'medium' : 'low';
}

function formatKobo(value: string): string {
  const amount = BigInt(value); const absolute = amount < 0n ? -amount : amount;
  const whole = absolute / 100n; const fraction = (absolute % 100n).toString().padStart(2, '0');
  return `${amount < 0n ? '-' : ''}₦${whole.toLocaleString('en-NG')}.${fraction}`;
}
